<template>
  <div class="bg-white-1 border-beige-10 rounded-sm border border-solid">
    <div
      class="border-beige-10 flex items-center justify-between gap-2 border-b border-solid px-4 py-3"
    >
      <h4 class="text-heading-1 text-beige-90">
        {{ segmentStore.segment?.is_locked ? "Users" : "Segment logic" }}
      </h4>
      <RfButtonNew v-if="canEdit" type="plain" size="none" @click="showHideForm">
        <RfChevronIcon
          class="h-6! w-6! -rotate-90 transition-transform"
          :class="{ 'rotate-90': segmentEditMode }"
        />
      </RfButtonNew>
    </div>
    <RfSegmentBuilder
      v-if="!segmentStore.segment?.is_locked"
      v-model:updating="updating"
      :segmentEditMode="segmentEditMode"
      @showHideForm="showHideForm"
      @segmentSaved="onSegmentSaved"
    />
    <div class="segment-overview-stat-row justify-start!">
      <div v-if="isPinpoint" class="segment-performance-stats">
        <div class="performance-stats-label whitespace-nowrap">Total Endpoints</div>
        <div class="performance-stats-value mt-1 flex items-center">
          <template v-if="segmentStore.getMetricsLoading">
            <RfBaseSkeleton width="60" height="12" srLabel="Loading statistics" />
          </template>
          <template v-else>
            <span class="decoration-grey-2">
              {{ totalEndpointsText }}
            </span>
          </template>
        </div>
      </div>
      <div class="segment-performance-stats">
        <div class="performance-stats-label whitespace-nowrap">Total Users</div>
        <div class="performance-stats-value" :class="{ 'mt-1 flex items-center': isPinpoint }">
          <template v-if="isPinpoint && segmentStore.getMetricsLoading">
            <RfBaseSkeleton width="60" height="12" />
          </template>
          <template v-else>
            <span class="decoration-grey-2">
              {{ totalUsersText }}
            </span>
          </template>
        </div>
      </div>
      <div v-if="updating">
        <v-icon size="12" class="text-grey-2 mr-1 ml-3">fa:fas fa-info-circle</v-icon>
        <span class="text-grey-2">Save to refresh counts</span>
      </div>
      <div v-if="!isPinpoint" class="chart-filter-wrapper">
        <div class="chart-filter-date-select">
          <div class="chart-filter-label">Date range:</div>
          <RfMetricsDateRangeSelector
            :key="model.id"
            parentPage="segments"
            :sinceStartDate="false"
            @fetchMetrics="fetchMetrics"
            @fetchMetricsCustom="fetchMetricsCustom"
          />
        </div>
        <div v-if="isValidComparison(currChartSrc)" class="compare-chart-opt">
          <span class="compare-chart-label">Compare with:</span>
          <v-checkbox
            v-if="getCompareChartVal"
            v-model="compareChartSrc"
            :trueValue="getCompareChartVal"
            :falseValue="null"
            :label="getCompareChartLabel"
            hideDetails
          ></v-checkbox>
        </div>
      </div>
    </div>

    <template v-if="!isPinpoint && getMetrics">
      <v-divider />
      <div class="rf-chart-wrapper">
        <v-row>
          <v-col cols="12">
            <RfSegmentChart
              :usage_type="usage_type"
              :metrics="getMetrics"
              :metricsKey="currChartSrc"
              :compareMetricsKey="compareChartSrc"
              :singleDayCustom="singleDayCustom"
            />
          </v-col>
        </v-row>
      </div>
    </template>
  </div>
</template>

<script>
import RfSegmentChart from "@/components/RfSegmentChart.vue";
import MetricsUtils from "@/utils/MetricsUtils";
import RfMetricsDateRangeSelector from "@/components/RfCommonCards/RfMetricsDateRangeSelector.vue";
import RfSegmentBuilder from "@/components/RfSegments/RfSegmentBuilder.vue";
import { useUserSettings } from "@/utils/composables/useUserSettings";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import { toLocaleNumberString } from "@/utils/stringHelpers";
import { useAppsStore } from "@/pinia/appsStore";
import { useSegmentStore } from "@/pinia/segmentStore";
import { useCustomFields } from "@/utils/composables/useCustomFields";
import { ref } from "vue";
import RfButtonNew from "../buttons/RfButtonNew.vue";
import RfChevronIcon from "../icons/RfChevronIcon.vue";

export default {
  name: "RfLiveStatistics",
  components: {
    RfSegmentChart,
    RfMetricsDateRangeSelector,
    RfSegmentBuilder,
    RfBaseSkeleton,
    RfButtonNew,
    RfChevronIcon,
  },
  props: ["metrics", "model", "usage_type", "currChartSrc"],
  setup: () => {
    const { getUserSetting, setUserSetting } = useUserSettings();
    return {
      appsStore: useAppsStore(),
      segmentStore: useSegmentStore(),
      customFields: useCustomFields(),
      updating: ref(),
      getUserSetting,
      setUserSetting,
    };
  },
  data() {
    return {
      compareChartSrc: null,
      compareKey: "segmentsCompare",
      usageKey: "segmentsUsage",
      singleDayCustom: false,
      segmentEditMode: true,
    };
  },
  computed: {
    isPinpoint() {
      return this.model?.segment_type === "pinpoint";
    },
    canEdit() {
      return !this.model.is_locked;
    },
    getCompareChartVal() {
      return MetricsUtils.Comparisons[this.currChartSrc];
    },
    getCompareChartLabel() {
      const result = MetricsUtils.All_Periods.find(item => item.value === this.getCompareChartVal);
      return result?.name || null;
    },
    totalUsers() {
      if (this.isPinpoint) {
        if (this.appsStore.app.flags["push_notification_sample_data"]) {
          const channels = this.model.filter.push?.channel?.length;
          return channels ? channels * 150000 : 450000;
        }
        return this.metrics?.users?.current;
      }
      return this.metrics?.users?.[this.currChartSrc]?.uniques || 0;
    },
    totalEndpoints() {
      if (this.appsStore.app.flags["push_notification_sample_data"]) {
        const channels = this.model.filter.push?.channel?.length;
        return channels ? channels * 250000 : 750000;
      }
      return this.metrics?.endpoints?.current;
    },
    totalUsersText() {
      return this.totalUsers ? toLocaleNumberString(this.totalUsers) : "N/A";
    },
    totalEndpointsText() {
      return this.totalEndpoints ? toLocaleNumberString(this.totalEndpoints) : "N/A";
    },
    getMetrics() {
      if (!this.metrics || Object.keys(this.metrics).length === 0) return [];
      return this.metrics[this.usage_type];
    },
  },
  watch: {
    compareChartSrc(to, from) {
      if (to !== from) {
        this.setUserSetting(this.compareKey, to);
      }
    },
  },
  mounted() {
    const filter = this.getUserSetting(this.compareKey);
    if (filter) {
      this.compareChartSrc = filter;
    }
    if (this.isPinpoint) {
      this.fetchPinpointMetrics();
    }
  },
  methods: {
    showHideForm() {
      this.segmentEditMode = !this.segmentEditMode;
    },
    isValidComparison(metric) {
      return !!MetricsUtils.Comparisons[metric];
    },
    fetchMetrics(timePeriod) {
      this.singleDayCustom = false;
      if (timePeriod === "since_start_date" && this.model.start_date) {
        const now = new Date();
        const datestring = `${now.getFullYear()}-${`0${now.getMonth() + 1}`.slice(
          -2,
        )}-${`0${now.getDate()}`.slice(-2)}`;
        this.singleDayCustom = datestring === this.model.start_date;
      }

      if (this.compareChartSrc) {
        this.compareChartSrc = MetricsUtils.Comparisons[timePeriod];
      }
      if (!this.metrics[timePeriod]) {
        const metric_periods = [
          { period: timePeriod },
          ...(MetricsUtils.Comparisons[timePeriod]
            ? [{ period: MetricsUtils.Comparisons[timePeriod] }]
            : []),
        ];

        const custom_field_id = this.isValidUsage(this.getUserSetting(this.usageKey));
        this.segmentStore.getMetrics({
          appId: this.$route.params.aid,
          id: this.$route.params.sid,
          params: { metric_periods, custom_field_id },
        });
      }
      this.$emit("update:curr-chart-src", timePeriod);
      this.$emit("update:date-range", null);
    },
    fetchMetricsCustom(timePeriod) {
      if (timePeriod.length) {
        this.singleDayCustom = timePeriod[0] === timePeriod[1];

        const custom_field_id = this.isValidUsage(this.getUserSetting(this.usageKey));
        this.segmentStore.getMetrics({
          appId: this.$route.params.aid,
          id: this.$route.params.sid,
          params: {
            metric_periods: [{ period: "custom", date_range: timePeriod }],
            custom_field_id,
          },
        });
      }
      this.$emit("update:curr-chart-src", "custom");
      this.$emit("update:date-range", timePeriod);
    },
    fetchPinpointMetrics() {
      this.segmentStore.getMetrics(
        {
          appId: this.$route.params.aid,
          id: this.$route.params.sid,
        },
        { nonBlocking: true },
      );
    },
    isValidUsage(customFieldId) {
      if (!this.appsStore.app || !customFieldId) return null;
      const field = this.customFields.appCustomFields.value
        .filter(item => item.connector_type === "redfast" && item.is_usage)
        .find(item => item.id === customFieldId);
      if (field) return field.id;
      return null;
    },
    onSegmentSaved() {
      if (this.isPinpoint) {
        this.fetchPinpointMetrics();
      }
    },
  },
};
</script>
